import './ImageViewer.scss';
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {PagesController} from '../pages-controller/PagesController';
import {TransformComponent, TransformWrapper} from 'react-zoom-pan-pinch';
import {debounce} from 'lodash';
import {computePageRotation, getErrorMessage} from '../../utils/utils';
import {fetchFile} from '../../services/fetcher';
import {useAuthContext} from '../../auth/AuthContextProvider';
import {getFilePreviewApiUrl} from '../../services/search-service';
import {SearchPageType} from '../../search/search-config';

interface ImageViewerProps {
    imageLink?: string;
}

export const ImageViewer = memo(({imageLink}: ImageViewerProps) => {
    const {authService} = useAuthContext();
    const authEnabled = authService?.isEnabled() ?? false;
    const [percentage, setPercentage] = useState<number>(100);
    const [rotation, setRotation] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const debounceWaitingTime = 75;

    const setPercentageDebounced = useMemo(
        () =>
            debounce((scale: number) => {
                setPercentage(Math.abs(100 * scale));
            }, debounceWaitingTime),
        [setPercentage],
    );

    const drawRotatedImage = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx || !imageRef.current) {
            return;
        }
        const img = imageRef.current;
        const angleInRadians = (rotation * Math.PI) / 180;
        const sin = Math.abs(Math.sin(angleInRadians));
        const cos = Math.abs(Math.cos(angleInRadians));

        canvas.width = img.height * sin + img.width * cos;
        canvas.height = img.height * cos + img.width * sin;

        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2); // Move origins to the center
        ctx.rotate(angleInRadians);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        ctx.restore();

        setIsLoading(false);
    }, [rotation]);

    const setImageWithAuth = async (previewLink: string) => {
        try {
            const blob: Blob = await fetchFile(
                getFilePreviewApiUrl(SearchPageType.BASIC),
                JSON.stringify({link: previewLink}),
            );
            return URL.createObjectURL(blob);
        } catch (error: any) {
            console.log('Error: ', getErrorMessage(error));
            return '';
        }
    };

    const setImage = useCallback(
        (url: string) => {
            const img = new Image();
            img.src = url;
            img.onload = () => {
                imageRef.current = img;
                drawRotatedImage();
            };
            img.onerror = () => {
                setIsLoading(false);
            };
        },
        [drawRotatedImage],
    );

    const setImageAsync = useCallback(
        async (imageLink: string) => {
            const url = await setImageWithAuth(imageLink);
            setImage(url);
        },
        [setImage],
    );

    useEffect(() => {
        if (!imageLink) {
            return;
        }
        setIsLoading(true);

        if (authEnabled) {
            void setImageAsync(imageLink);
        } else {
            setImage(imageLink);
        }
    }, [authEnabled, imageLink, setImage, setImageAsync]);

    useEffect(() => {
        drawRotatedImage();
    }, [rotation, drawRotatedImage]);

    useEffect(() => {
        // Cleanup function to cancel any pending debounced calls
        return () => {
            setPercentageDebounced.cancel();
        };
    }, [setPercentageDebounced]);

    const handleRotation = useCallback((rotation: number) => {
        setRotation((prevRotation) => computePageRotation(prevRotation, rotation));
    }, []);

    const handleTransformed = useCallback(
        (_, event) => {
            setPercentageDebounced(event.scale);
        },
        [setPercentageDebounced],
    );

    return (
        <div id={'HibuImagePreviewWrapper'}>
            <TransformWrapper
                wheel={{disabled: true}}
                doubleClick={{disabled: true}}
                limitToBounds={true}
                onTransformed={handleTransformed}
                panning={{activationKeys: ['Control', 'Alt']}}
            >
                {({zoomIn, zoomOut, resetTransform}) => (
                    <div className="transform-wrapper">
                        <TransformComponent
                            wrapperClass="transform-component-wrapper"
                            contentClass="transform-component-content"
                        >
                            <div className="image-container">
                                <canvas
                                    ref={canvasRef}
                                    id="HibuImagePreview"
                                    className={isLoading ? '' : 'image-loaded'}
                                />
                            </div>
                        </TransformComponent>
                        {isLoading ? (
                            <></>
                        ) : (
                            <PagesController
                                showPagesButtons={false}
                                percentage={percentage}
                                handleZoomIn={zoomIn}
                                handleZoomOut={zoomOut}
                                handleResetZoomAndPan={resetTransform}
                                handlePageRotation={handleRotation}
                            />
                        )}
                    </div>
                )}
            </TransformWrapper>
        </div>
    );
});
